/**
 * Centralized TanStack Query key factory.
 *
 * These keys are used by hooks to watch PowerSync tables
 * and auto-invalidate via TanStack Query.
 *
 * Rules:
 *   - Every PowerSync-backed or server-backed query MUST use a key from here.
 *   - Never use inline string arrays as queryKey — use this factory instead.
 *   - Keys are grouped by domain and ordered from broad → specific.
 */
export const queryKeys = {
  domain: {
    /** Synchronized church tree (subtree visible to the account). */
    churches: (userId: string | undefined) => ["domain", "churches", userId] as const,
    /** Visible venues. */
    venues: (userId: string | undefined) => ["domain", "venues", userId] as const,
    /** Visible projection items (`projection` capability). */
    projectionItems: (userId: string | undefined) => ["domain", "projection", userId] as const,
    /** Effective permissions of the active account (local effective_grant). */
    effectiveGrants: (userId: string | undefined) => ["domain", "effectiveGrants", userId] as const,
    /** Relationships between visible churches. */
    churchRelations: (userId: string | undefined) => ["domain", "churchRelations", userId] as const,
    /** Images of visible venues. */
    venueImages: (userId: string | undefined) => ["domain", "venueImages", userId] as const,
    /** People directory + family relationships + memberships. */
    people: (userId: string | undefined) => ["domain", "people", userId] as const,
    personRelations: (userId: string | undefined) => ["domain", "personRelations", userId] as const,
    memberships: (userId: string | undefined) => ["domain", "memberships", userId] as const,
    /** Global catalogs (capabilities/group categories). */
    catalogs: () => ["domain", "catalogs"] as const,
    groupTypes: (userId: string | undefined) => ["domain", "groupTypes", userId] as const,
    groups: (userId: string | undefined) => ["domain", "groups", userId] as const,
    groupMembers: (userId: string | undefined) => ["domain", "groupMembers", userId] as const,
    /** Music: group library (songs/parts/setlists) + rehearsals. */
    songs: (userId: string | undefined) => ["domain", "songs", userId] as const,
    songParts: (userId: string | undefined) => ["domain", "songParts", userId] as const,
    rehearsals: (userId: string | undefined) => ["domain", "rehearsals", userId] as const,
    setlists: (userId: string | undefined) => ["domain", "setlists", userId] as const,
    setlistSongs: (userId: string | undefined) => ["domain", "setlistSongs", userId] as const,
    /** Meetings + recurrence + activities + photos + attendance. */
    meetings: (userId: string | undefined) => ["domain", "meetings", userId] as const,
    meetingExceptions: (userId: string | undefined) =>
      ["domain", "meetingExceptions", userId] as const,
    meetingInvites: (userId: string | undefined) => ["domain", "meetingInvites", userId] as const,
    meetingActivities: (userId: string | undefined) =>
      ["domain", "meetingActivities", userId] as const,
    activityPhotos: (userId: string | undefined) => ["domain", "activityPhotos", userId] as const,
    attendanceSessions: (userId: string | undefined) =>
      ["domain", "attendanceSessions", userId] as const,
    attendanceEntries: (userId: string | undefined) =>
      ["domain", "attendanceEntries", userId] as const,
    /** F2 — Service: meeting schedule + prayer requests. */
    scheduleBlocks: (userId: string | undefined) => ["domain", "scheduleBlocks", userId] as const,
    prayerRequests: (userId: string | undefined) => ["domain", "prayerRequests", userId] as const,
    /** F3 — Administration: finances (treasurer) + inventory. */
    financeRecords: (userId: string | undefined) => ["domain", "financeRecords", userId] as const,
    funds: (userId: string | undefined) => ["domain", "funds", userId] as const,
    financeCategories: (userId: string | undefined) =>
      ["domain", "financeCategories", userId] as const,
    inventoryItems: (userId: string | undefined) => ["domain", "inventoryItems", userId] as const,
    inventoryMovements: (userId: string | undefined) =>
      ["domain", "inventoryMovements", userId] as const,
    /** F4 — Discipleship: devotionals + theater/dance. */
    devotionalPlans: (userId: string | undefined) => ["domain", "devotionalPlans", userId] as const,
    devotionalItems: (userId: string | undefined) => ["domain", "devotionalItems", userId] as const,
    devotionalProgress: (userId: string | undefined) =>
      ["domain", "devotionalProgress", userId] as const,
    devotionalFollowups: (userId: string | undefined) =>
      ["domain", "devotionalFollowups", userId] as const,
    sceneWorks: (userId: string | undefined) => ["domain", "sceneWorks", userId] as const,
    sceneElements: (userId: string | undefined) => ["domain", "sceneElements", userId] as const,
    scenePhotos: (userId: string | undefined) => ["domain", "scenePhotos", userId] as const,
    /** F5-residual — Cleaning: shift roster + assignments. */
    cleaningShifts: (userId: string | undefined) => ["domain", "cleaningShifts", userId] as const,
    cleaningAssignments: (userId: string | undefined) =>
      ["domain", "cleaningAssignments", userId] as const,
    /** Roles and grants (permission administration). */
    roles: (userId: string | undefined) => ["domain", "roles", userId] as const,
    accountRoles: (userId: string | undefined) => ["domain", "accountRoles", userId] as const,
    permissionGrants: (userId: string | undefined) =>
      ["domain", "permissionGrants", userId] as const,
  },
  activity: {
    all: ["activity"] as const,
    /** Local feed (activity_recent). Includes userId: isolates cache between accounts. */
    feed: (userId: string | undefined, filter: Record<string, unknown> = {}) =>
      ["activity", "feed", userId, filter] as const,
    /** Local history of a specific entity. */
    entity: (userId: string | undefined, entityType: string, entityId: string | undefined) =>
      ["activity", "entity", userId, entityType, entityId] as const,
  },
  powersync: {
    /** Upload queue stats (sync indicator). */
    uploadStats: (userId: string | undefined) => ["powersync", "uploadStats", userId] as const,
    /** Is there local data? (cleanup button in profile). */
    hasLocalData: (userId: string | undefined) => ["powersync", "hasLocalData", userId] as const,
  },
  themes: {
    all: ["themes"] as const,
    /** Full theme catalog (getThemesQuery without pagination; shared cache). */
    catalog: () => ["themes", "catalog"] as const,
    /** Gallery of all available themes (paginated). */
    gallery: (limit?: number) => ["themes", "gallery", limit] as const,
    /** Active theme row resolved for a specific user. */
    active: (userId: string | undefined) => ["themes", "active", userId] as const,
    /** Raw user_settings row (stores selected theme id). */
    userSettings: (userId: string | undefined) => ["themes", "userSettings", userId] as const,
  },
  userPreferences: {
    /** Consolidated user_preferences row (language, font, theme). */
    consolidated: (userId: string | undefined) =>
      ["userPreferences", "consolidated", userId] as const,
  },
  fonts: {
    all: ["fonts"] as const,
    families: (category?: string) => ["fonts", "families", category] as const,
    family: (id: string | null) => ["fonts", "family", id] as const,
    familyVariants: (id: string | null) => ["fonts", "variants", id] as const,
    variants: () => ["fonts", "variants"] as const,
    /** Active font family for a user (from user_preferences). */
    userFamily: (userId: string | undefined) => ["fonts", "userFamily", userId] as const,
    /** Full font families catalog (profile font selector). */
    catalogFamilies: () => ["fonts", "catalogFamilies"] as const,
    /** Full font variants catalog (profile font selector). */
    catalogVariants: () => ["fonts", "catalogVariants"] as const,
    /** Presence probe: is the synced catalog available locally at all? */
    catalogPresence: () => ["fonts", "catalogPresence"] as const,
  },
  /** Admin font management (server REST, not PowerSync). */
  adminFonts: {
    /** The approved catalog (listAll). Prefix of `registry` so invalidating it
     *  cascades to both lists. */
    all: ["admin", "fonts"] as const,
    /** Third-party registry browser, keyed by the active filters. */
    registry: (search: string, category: string) =>
      ["admin", "fonts", "registry", search, category] as const,
  },
  auth: {
    invitations: () => ["auth", "listInvitations"] as const,
    organizations: () => ["auth", "listOrganizations"] as const,
    members: (organizationId: string) =>
      ["auth", "getOrganizationMembers", { organizationId }] as const,
    session: () => ["auth", "session"] as const,
    socialProviders: () => ["auth", "socialProviders"] as const,
    loginMethods: () => ["auth", "loginMethods"] as const,
  },
};
